import { DtoEntry, DtoIndividualReportDataResponse, DtoTestSet } from "@/types";
import { getQuestionsDataForCategory } from "../../FuturebilityCockpit/hooks/useHydratedCubeData/reports-utils";
import { useReflectionPersonalSummaryData } from "../../FuturebilityCockpit/hooks/useReflectionPersonalSummaryData";
import { useTopicsWithScore } from "../../FuturebilityCockpit/hooks";
import { ReportQuestion } from "@/routes/Frontend/FuturebilityCockpit/hooks/useHydratedCubeData/types";

export interface ReflectionIndividualReportDataResponse {
  personalSummary: {
    chartData: ReturnType<typeof useReflectionPersonalSummaryData>;
    questions: ReportQuestion[];
  };
  topicsWithAnswers: {
    leverages: TopicWithAnswer[];
    challenges: TopicWithAnswer[];
  };
}

export interface ReflectionIndividualReportDataArgs {
  rawData?: DtoIndividualReportDataResponse;
  sessionId: string;
}

export interface TopicWithAnswer {
  title: string;
  questions: Array<{
    title: string;
    answer: string | undefined;
  }>;
}

export function getAnswerFromEntries(
  allEntries: DtoEntry[],
  testSetId: string,
  dbColumn: string
): string | undefined {
  return allEntries.find(entry => entry.testSetId === testSetId)?.[
    dbColumn as `T${number}`
  ];
}

function mapTestSetsToTopicsWithAnswers(
  testSets: DtoTestSet[],
  allEntries: DtoEntry[]
): TopicWithAnswer[] {
  return testSets.map(ts => ({
    title: ts.info?.name || "N/A",
    questions: ts.items.map(item => ({
      title: item.text,
      answer: getAnswerFromEntries(allEntries, ts._id, item.dbColumn),
    })),
  }));
}

export function useReflectionTextReplies({
  rawData,
}: ReflectionIndividualReportDataArgs) {
  const test = rawData?.tests[0]!;
  const testSets = rawData?.testSets ?? [];
  const allEntries = rawData?.allEntries ?? [];

  const topicWithScoreCommonProps = {
    currentTestId: test._id,
    selectedFilters: undefined,
    numberOfTopics: 3,
    order: "ASC",
    rawData: {
      testSets,
      allEntries,
      tests: [test],
      client: rawData?.client!,
      dynamicTestSets: [],
    },
  };

  const challenges = useTopicsWithScore({
    ...topicWithScoreCommonProps,
    order: "ASC",
  });

  const leverages = useTopicsWithScore({
    ...topicWithScoreCommonProps,
    order: "DESC",
  });

  const challengesIds = challenges.map(c => c._id);
  const leveragesIds = leverages.map(l => l._id);

  const reflectionChallengesTestSets =
    rawData?.reflectionTestSets.filter(
      ts => ts.originalTestSetId && challengesIds.includes(ts.originalTestSetId)
    ) ?? [];
  const reflectionLeveragesTestSets =
    rawData?.reflectionTestSets.filter(
      ts => ts.originalTestSetId && leveragesIds.includes(ts.originalTestSetId)
    ) ?? [];

  return {
    leverages: mapTestSetsToTopicsWithAnswers(
      reflectionLeveragesTestSets,
      allEntries
    ),
    challenges: mapTestSetsToTopicsWithAnswers(
      reflectionChallengesTestSets,
      allEntries
    ),
  };
}

export function useReflectionIndividualData({
  rawData,
  sessionId,
}: ReflectionIndividualReportDataArgs): ReflectionIndividualReportDataResponse {
  const chartData = useReflectionPersonalSummaryData({
    entries: rawData?.allEntries ?? [],
    sessionId,
  });

  const topicsWithAnswers = useReflectionTextReplies({ rawData, sessionId });
  const personalSummaryTestSet = rawData?.reflectionTestSets.find(
    ts => !ts.templateTestSetId
  );
  const individualSummaryReportItems = getQuestionsDataForCategory(
    personalSummaryTestSet!,
    rawData?.allEntries ?? [],
    items => items
  );

  return {
    personalSummary: {
      chartData,
      questions: individualSummaryReportItems.questions,
    },
    topicsWithAnswers,
  };
}
